import { marketLocalDateKey } from "../markets/market-local-date.mjs";
import { normalizeDashboardBriefingV2Payload } from "./dashboard-briefing-v2.mjs";
import type { AudioQueueEntry, QueuedAudioRequest } from "./audio-types";

export const CURRENT_WEB_AUDIO_QUEUE_CONTRACT = 2 as const;

function text(value: unknown): string {
  return String(value || "").trim();
}

function normalizeQueuedRequest(value: unknown): QueuedAudioRequest | null {
  const request = value && typeof value === "object" ? (value as Partial<QueuedAudioRequest>) : null;
  const path = text(request?.path);
  const fallbackCode = text(request?.fallbackCode);
  const body =
    request?.body && typeof request.body === "object" && !Array.isArray(request.body)
      ? (request.body as Record<string, unknown>)
      : null;
  if (!path.startsWith("/api/") || !fallbackCode || !body) return null;
  const normalizedBody =
    path === "/api/dashboard-briefing-audio" ? normalizeDashboardBriefingV2Payload(body) : body;
  if (!normalizedBody) return null;
  return {
    path,
    body: normalizedBody,
    fallbackCode,
    downloadName: text(request?.downloadName) || undefined,
  };
}

export function normalizeAudioQueueEntry(
  value: unknown,
  createId: () => string,
): AudioQueueEntry | null {
  const entry = value && typeof value === "object" ? (value as Partial<AudioQueueEntry>) : null;
  const queuedAtEpochMs = Number(entry?.queuedAtEpochMs) || 0;
  const key = text(entry?.key);
  const label = text(entry?.label);
  const request = normalizeQueuedRequest(entry?.request);
  if (
    entry?.contractVersion !== CURRENT_WEB_AUDIO_QUEUE_CONTRACT ||
    !queuedAtEpochMs ||
    !key ||
    !label ||
    !request
  ) {
    return null;
  }
  return {
    contractVersion: CURRENT_WEB_AUDIO_QUEUE_CONTRACT,
    id: text(entry?.id) || createId(),
    key,
    label,
    detail: text(entry?.detail) || undefined,
    queuedAtEpochMs,
    request,
  };
}

export function queuedAudioEntryIsCurrentMarketDay(
  entry: AudioQueueEntry,
  nowEpochMs = Date.now(),
): boolean {
  if (entry.request.path !== "/api/dashboard-briefing-audio") return true;
  const country = text(entry.request.body.country);
  return (
    marketLocalDateKey(entry.queuedAtEpochMs, country) ===
    marketLocalDateKey(nowEpochMs, country)
  );
}
